import * as R from 'ramda'
import { formatPrice } from 'sidecar-utility-library'

const packageInfo = {
  access: {
    order: [
      'basicPackage',
      'essentialPackage',
      'premiumPackage',
      'customizedPackage',
    ],
    packageNames: {
      basicPackage: 'Budget',
      essentialPackage: 'Standard',
      premiumPackage: 'Premium',
      customizedPackage: 'Custom',
    },
    paymentSchedules: {
      ANNUAL: '/year',
      MONTHLY: '/mo',
      QUARTERLY: '/3 mo',
      SEMI_ANNUAL: '/6 mo',
    },
    paymentSchedulesFull: {
      QUARTERLY: '3 months',
      SEMI_ANNUAL: '6 months',
      ANNUAL: 'year',
      MONTHLY: 'month',
    },
    paymentScheduleMap: {
      ANNUAL: 'annual',
      MONTHLY: 'monthly',
      QUARTERLY: 'quarterly',
      SEMI_ANNUAL: 'semiAnnual',
    },
    paymentScheduleLongMap: {
      ANNUAL: 'every year',
      MONTHLY: 'every month',
      QUARTERLY: 'every 3 months',
      SEMI_ANNUAL: 'every 6 months',
    },
    packageDescriptions: {
      basicPackage:
        'For those who want to cover only their basic medical needs, plus a little extra in case of a minor emergency.',
      customizedPackage:
        'For those who want to customize their coverage for their specific needs.',
      essentialPackage:
        'For those who want to cover more frequent medical needs and be prepared for small to medium emergencies or procedures.',
      premiumPackage:
        'For those who want to cover frequent medical needs and be prepared for major emergencies or procedures.',
    },
    maxAnnualAllowanceOptionsWithYear: [
      { label: '$5,000/year', value: 5000 },
      { label: '$10,000/year', value: 10000 },
      { label: '$15,000/year', value: 15000 },
      { label: '$20,000/year', value: 20000 },
      { label: '$25,000/year', value: 25000 },
      { label: '$30,000/year', value: 30000 },
      { label: '$35,000/year', value: 35000 },
      { label: '$40,000/year', value: 40000 },
      { label: '$45,000/year', value: 45000 },
      { label: '$50,000/year', value: 50000 },
      { label: '$2,000,000/year', value: 2000000 },
    ],
    policyDisplayNames: {
      basicPackage: 'BASIC',
      customizedPackage: 'CUSTOMIZED',
      essentialPackage: 'ESSENTIAL',
      premiumPackage: 'PREMIUM',
    },
  },
  offExchange: {
    order: ['bronzePackage', 'silverPackage', 'goldPackage'],
    packageNames: {
      bronzePackage: 'Bronze',
      silverPackage: 'Silver',
      goldPackage: 'Gold',
    },
    paymentSchedules: {
      MONTHLY: '/mo',
    },
    paymentSchedulesFull: {
      MONTHLY: 'month',
    },
    paymentScheduleMap: {
      MONTHLY: 'monthly',
    },
    paymentScheduleLongMap: {
      MONTHLY: 'every month',
    },
    packageDescriptions: {},
    policyDisplayNames: {
      bronzePackage: 'BRONZE',
      silverPackage: 'SILVER',
      goldPackage: 'GOLD',
    },
  },
}

// This is an easy way to generate consistent data-testids for all the properties we are using
export const getTestId = property => `info_${property}`

// This is an easy way to generate a selector for components that
// used the getTestId function for their data-testids
export const formatTestId = property => `[data-testid="${getTestId(property)}"]`

// This is an easy way to easily take either a policy or pendingChange to extract
// the cost and paymentSchedule to read like a Recurring payment i.e. $300/month
export const formatPolicyCost = ({ cost, paymentSchedule }) => {
  if (!R.isNil(cost) && !R.isNil(paymentSchedule)) {
    return `${formatPrice(cost)}${
      packageInfo['access']['paymentSchedules'][paymentSchedule]
    }`
  }
}

// works with either policy or pendingChange object to extract all prescriptions taken
export const getPrescriptionsTaken = R.pipe(
  R.path(['medicalHistory', 'prescriptions']),
  R.ifElse(
    R.isNil,
    R.always(null),
    R.pipe(
      R.map(({ name }) => name),
      R.join(', '),
      R.when(R.isEmpty, R.always('None')),
    ),
  ),
)

// Test utils

// For use in test files to determine if a change-delta exists
export const doesChangeExist = Component => property =>
  Component.exists(formatTestId(property))

// For use in test files to determine what the text value is of a change-delta
export const getChangeValue = Component => (property, pendingOrCurrent) =>
  Component.find(
    `${formatTestId(property)} div > [data-testid="${pendingOrCurrent}"]`,
  ).text()

export const getChangeTestId = (property, pendingOrCurrent) =>
  `${formatTestId(property)} div > [data-testid="${pendingOrCurrent}"]`
